import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Backdrop,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { collection, onSnapshot, orderBy, query, where, updateDoc, doc, getDocs, getDoc, serverTimestamp, addDoc, getCountFromServer } from "firebase/firestore";
import { BatchesRef, db, importQuicktaleBatch, storage } from "../../../../firebase";
import { ref, uploadBytes } from "firebase/storage";
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../firebase';

import Header from "../../../../common/Header";
import Sidebar from "../../../../common/Sidebar";
import TablePaginationSection from "../../../../common/TablePaginationSection";
import TableHeadComponent from "../../../../common/TableHeadComponent";
import { mainLayoutStyles, showNotification, routes } from "../../../../constants";
import { useAuth } from "../../../../hooks";
import ImportBatchModal from "../ImportBatchModal/ImportBatchModal";
import SearchBar from "../../components/SearchBar/SearchBar";
import FilterSection from "../../components/FilterSection/FilterSection";
import FilterModalComponent from "../../../../common/FilterModalComponent";
import { getDateRange, getSingleOrg } from "../../../OrgProf/hook/Organizationhook";
import BatchReportModal from "../../components/BatchReportModal/BatchReportModal";

const headCells = [
  { id: "batchName", label: "Batch Name", numeric: false, disablePadding: true, width: 260 },
  { id: "description", label: "Description", numeric: false, disablePadding: true, width: 280 },
  { id: "createdAt", label: "Creation Date", numeric: false, disablePadding: true, width: 180 },
  { id: "status", label: "Status", numeric: false, disablePadding: true, width: 100 },
  { id: "archive", label: "Active/Archived", numeric: false, disablePadding: true, width: 130 },
  { id: "quicktalesCount", label: "Total", numeric: true, disablePadding: true, width: 90 },
  { id: "quicktalesImported", label: "Imported", numeric: true, disablePadding: true, width: 90 },
  { id: "quicktalesSkipped", label: "Skipped", numeric: true, disablePadding: true, width: 90 },
  { id: "report", label: "Report", numeric: false, disablePadding: true, width: 90 },
  { id: "createdBy", label: "Created By", numeric: false, disablePadding: true, width: 140 },
];

const BatchesContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, organizationId } = useAuth();

  const [batches, setBatches] = useState([]);
  const [allBatches, setAllBatches] = useState([]); // Store all batches for filtering
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pendingBatchCsvPath, setPendingBatchCsvPath] = useState(null);
  
  // Filter and search states
  const [filterModal, setFilterModal] = useState(false);
  const [statusFilterOptions, setStatusFilterOptions] = useState([]);
  const [createdDateFilterOptions, setCreatedDateFilterOptions] = useState([]);
  const [archiveFilterOptions, setArchiveFilterOptions] = useState(["Active"]); // Default to Active (not archived)
  
  // Report modal states
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedBatchName, setSelectedBatchName] = useState("");

  // Areas for Default Area selector
  const [areas, setAreas] = useState([]);

  const orgId = organizationId || currentUser?.organization || null;

  // Org-level default for the Import modal's Auto-Approve toggle.
  const [orgImportBatchAutoApprove, setOrgImportBatchAutoApprove] = useState(false);

  // Refresh the org setting each time the import modal opens so changes made on
  // the Org Profile page are reflected without a full reload.
  useEffect(() => {
    if (!orgId || !importModalOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const orgSnap = await getSingleOrg(orgId);
        if (!cancelled && orgSnap?.exists?.()) {
          setOrgImportBatchAutoApprove(!!orgSnap.data()?.importBatchAutoApprove);
        }
      } catch (error) {
        console.log("error loading org import auto-approve setting:", error?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, importModalOpen]);

  const normalizeAreaName = (value) => {
    try {
      return String(value || "")
        .trim()
        .replace(/\s+/g, " ");
    } catch {
      return "";
    }
  };

  const reservedAreaNames = new Set(["all", "nearby"]);

  const ensureAreaExists = async ({ orgId, areaNameRaw, userId }) => {
    const areaName = normalizeAreaName(areaNameRaw);
    if (!areaName) return;

    if (reservedAreaNames.has(areaName.toLowerCase())) {
      throw new Error('"All" and "Nearby" are reserved Area names.');
    }

    // Existing by exact match
    const existingQ = query(
      collection(db, "area"),
      where("organization", "==", orgId),
      where("name", "==", areaName)
    );
    const existingSnap = await getDocs(existingQ);
    if (!existingSnap.empty) return;

    // Position = (# existing area docs) + (All+Nearby) + 1
    let nextPosition = 1;
    try {
      const countQ = query(collection(db, "area"), where("organization", "==", orgId));
      const countSnap = await getCountFromServer(countQ);
      const docCount = countSnap?.data()?.count;
      if (typeof docCount === "number" && Number.isFinite(docCount)) {
        nextPosition = docCount + 3;
      }
    } catch (e) {
      // best effort fallback
      console.warn("ensureAreaExists: count failed, defaulting position=1", e);
    }

    await addDoc(collection(db, "area"), {
      name: areaName,
      organization: orgId,
      isActive: true,
      isDeleted: false,
      position: nextPosition,
      albumsAndQTs: [],
      createdAt: serverTimestamp(),
      createdBy: userId || "",
      updatedAt: serverTimestamp(),
      updatedBy: userId || "",
    });
  };

  // Disable Import Batch while any batch is processing (or while starting a new import)
  const hasProcessingBatch = useMemo(
    () => allBatches.some((b) => b.status === "processing"),
    [allBatches]
  );
  const isImportDisabled = importing || hasProcessingBatch;

  // Handle report view click
  const handleViewReport = (event, batch) => {
    event.stopPropagation(); // Prevent row click
    setSelectedReport(batch.report || []);
    setSelectedBatchName(batch.batchName);
    setReportModalOpen(true);
  };

  // Bulk action: Archive/Unarchive batches
  const handleArchiveToggle = async (shouldArchive) => {
    try {
      setLoading(true);
      const updatePromises = selected.map(async (batchId) => {
        const batchRef = doc(db, "batches", batchId);
        await updateDoc(batchRef, {
          archive: shouldArchive,
          updatedAt: serverTimestamp(),
        });
      });
      await Promise.all(updatePromises);
      setSelected([]);
      showNotification(`Batch(es) ${shouldArchive ? 'archived' : 'unarchived'} successfully.`);
    } catch (error) {
      console.error("Error toggling archive status:", error);
      showNotification("Error updating batches");
    } finally {
      setLoading(false);
    }
  };

  // Bulk action: Get links for selected batches
  const handleGetLink = async () => {
    setLoading(true);
    try {
      const generateExternalUpdateLink = httpsCallable(functions, 'generateExternalUpdateLink');
      const links = [];
      
      for (const batchId of selected) {
        const batch = allBatches.find(b => b.id === batchId);
        if (batch && batch.quicktalesImported > 0) {
          // Get first quicktale from this batch to generate link
          const batchQtQuery = query(
            collection(db, "batchQuicktales"),
            where("batchId", "==", batchId),
            where("narrator", "!=", "")
          );
          const snapshot = await getDocs(batchQtQuery);
          if (!snapshot.empty) {
            const qtId = snapshot.docs[0].id;
            const result = await generateExternalUpdateLink({
              quicktaleId: qtId,
              origin: window.location.origin,
            });
            links.push({
              link: result.data.link,
              title: batch.batchName,
            });
          }
        }
      }
      
      if (links.length > 0) {
        // You'll need to add GetLinksModal component
        console.log("Generated links:", links);
        showNotification(`Generated ${links.length} link(s)`);
      } else {
        showNotification("No valid batches with quicktales to generate links for");
      }
    } catch (error) {
      console.error("Error generating links:", error);
      showNotification("Error generating links");
    } finally {
      setLoading(false);
    }
  };

  // Bulk action: Send links for selected batches
  const handleSendLink = async () => {
    showNotification("Send link functionality coming soon");
    // This would require more complex logic to handle multiple batches
    // and their associated quicktales/narrators
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = paginatedBatches.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClickCheckbox = (event, id) => {
    event.stopPropagation(); // Prevent row click when clicking checkbox
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Search function
  const handleSearchBatches = (e) => {
    try {
      const value = e.target.value;
      if (value.length >= 1) {
        const searchTerm = value.toLowerCase();
        const filtered = allBatches.filter((batch) => {
          return (
            (batch.batchName && batch.batchName.toLowerCase().includes(searchTerm)) ||
            (batch.status && batch.status.toLowerCase().includes(searchTerm))
          );
        });
        setBatches(filtered);
      } else {
        setBatches(allBatches);
      }
    } catch (error) {
      console.log("error searching batches:", error.message);
    }
  };

  // Filter function
  const handleFilterBatches = () => {
    setSelected([]);
    let filtered = [...allBatches];

    // Apply archive filter (default to Active)
    if (archiveFilterOptions.length > 0 && !archiveFilterOptions.includes("All")) {
      if (archiveFilterOptions.includes("Active")) {
        // Show only non-archived batches (archive field is false or doesn't exist)
        filtered = filtered.filter((batch) => !batch.archive);
      } else if (archiveFilterOptions.includes("Archived")) {
        // Show only archived batches (archive field is true)
        filtered = filtered.filter((batch) => batch.archive === true);
      }
    }

    // Apply status filter
    if (statusFilterOptions.length > 0) {
      filtered = filtered.filter((batch) =>
        statusFilterOptions.includes(batch.status)
      );
    }

    // Apply created date filter
    if (createdDateFilterOptions.length > 0) {
      const dateRange = getDateRange(createdDateFilterOptions, "Created At");
      if (dateRange && dateRange.startDate && dateRange.endDate) {
        filtered = filtered.filter((batch) => {
          if (!batch.createdAt) return false;
          const batchDate = batch.createdAt.toDate ? batch.createdAt.toDate() : batch.createdAt;
          const batchTime = new Date(batchDate).getTime();
          return batchTime >= dateRange.startDate.getTime() && batchTime <= dateRange.endDate.getTime();
        });
      }
    }

    setBatches(filtered);
  };

  // Handle filter chip deletion
  const handleDeleteChip = (filterType, filterItem) => {
    if (filterType === "status") {
      setStatusFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    } else if (filterType === "createdDate") {
      setCreatedDateFilterOptions((prevOptions) =>
        prevOptions.filter((item) => item !== filterItem)
      );
    }
  };

  // Reset filters
  const resetFilter = () => {
    setArchiveFilterOptions(["Active"]); // Reset to default Active filter
    setStatusFilterOptions([]);
    setCreatedDateFilterOptions([]);
    setSelected([]);
    setFilterModal(false);
  };

  // Apply filters when filter options change or data is loaded
  useEffect(() => {
    if (allBatches.length > 0) {
      handleFilterBatches();
    }
  }, [allBatches, statusFilterOptions, createdDateFilterOptions, archiveFilterOptions]);

  // Subscribe to batches for this organization
  useEffect(() => {
    if (!orgId) {
      setBatches([]);
      setAllBatches([]);
      setLoading(false);
      return;
    }

    const q = query(
      BatchesRef,
      where("organization", "==", orgId),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const dataPromises = snapshot.docs.map(async (docSnapshot) => {
          const d = docSnapshot.data();

          let createdByData = null;
          if (d.createdBy) {
            try {
              const userDoc = await getDoc(doc(db, "users", d.createdBy));
              if (userDoc.exists()) {
                createdByData = userDoc.data();
              }
            } catch (error) {
              console.error("Error fetching createdBy user for batch:", error);
            }
          }

          return {
            id: docSnapshot.id,
            ...d,
            createdByData,
          };
        });

        const data = await Promise.all(dataPromises);
        setAllBatches(data); // Store all batches
        setLoading(false);
      },
      (error) => {
        //console.error("Error listening to batches:", error);
        //showNotification("Error loading batches");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [orgId]);

  // Subscribe to areas for this organization (for Default Area dropdown)
  useEffect(() => {
    if (!orgId) {
      setAreas([]);
      return;
    }

    const q = query(
      collection(db, "area"),
      where("organization", "==", orgId),
      orderBy("position")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAreas(data);
      },
      () => {
        // non-fatal
        setAreas([]);
      }
    );

    return unsubscribe;
  }, [orgId]);

  // When a pending batch's processing row appears in Firestore, close the modal and clear loading state
  useEffect(() => {
    if (!pendingBatchCsvPath) return;

    const matchingBatch = allBatches.find(
      (b) => b.csvFilePath === pendingBatchCsvPath && b.status === "processing"
    );

    if (matchingBatch) {
      showNotification("Batch import started. You can continue working while we process your file.");
      setImportModalOpen(false);
      setPendingBatchCsvPath(null);
      setImporting(false);
    }
  }, [allBatches, pendingBatchCsvPath]);

  // Auto-open import modal when navigated from sidebar "QuickTales Batch Import" under Create
  useEffect(() => {
    if (location.state?.openImportModal) {
      setImportModalOpen(true);
      // Clear the state to prevent re-triggering on back navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openImportModal]);

  const toggleArchiveFilter = () => {
    if (archiveFilterOptions.includes("All")) {
      setArchiveFilterOptions(["Active"]);
    } else {
      setArchiveFilterOptions(["All"]);
    }
  };

  const handleOpenImportModal = () => {
    setImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    if (importing) return;
    setImportModalOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 200));
    setPage(1);
  };

  const paginatedBatches = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return batches.slice(startIndex, startIndex + rowsPerPage);
  }, [batches, page, rowsPerPage]);

  const handleRowClick = (batch) => {
    navigate(routes.batchDetails.replace(":batchId", batch.id), {
      state: { batch },
    });
  };

  const handleImportBatch = async ({ batchName, description, file, defaultAreaName, autoApprove }) => {
    if (!orgId || !currentUser?.id) {
      showNotification("Missing organization or user context");
      return;
    }

    setImporting(true);

    try {
      const defaultAreaNameNormalized = normalizeAreaName(defaultAreaName);
      if (defaultAreaNameNormalized) {
        await ensureAreaExists({
          orgId,
          areaNameRaw: defaultAreaNameNormalized,
          userId: currentUser?.id,
        });
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
      const storagePath = `batch-imports/${orgId}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      // Track this CSV path so we can close the modal once its processing batch row appears
      setPendingBatchCsvPath(storagePath);

      const narratorOwnerUserId = currentUser?.userId || currentUser?.uid || currentUser?.id;

      // Start the callable but do not await its completion; we rely on Firestore snapshots
      importQuicktaleBatch({
        batchName,
        batchDescription: description || "",
        csvFilePath: storagePath,
        csvOriginalFilename: file.name,
        organizationId: orgId,
        adminId: currentUser.id,
        narratorOwnerUserId,
        defaultAreaName: defaultAreaNameNormalized,
        autoApprove: autoApprove === true,
      })
        .then((result) => {
          if (!result?.data?.success) {
            showNotification("Batch import failed to start");
            setPendingBatchCsvPath(null);
            setImporting(false);
          }
          // On success, the snapshot-based effect will close the modal when the processing row is visible
        })
        .catch((error) => {
          console.error("Error importing batch:", error);
          showNotification(error?.message || "Error importing batch");
          setPendingBatchCsvPath(null);
          setImporting(false);
        });
    } catch (error) {
      console.error("Error uploading CSV for batch import:", error);
      showNotification(error?.message || "Error uploading CSV for batch import");
      setPendingBatchCsvPath(null);
      setImporting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <Header mainTitle={"Batched QuickTales"} bgColor="#fff" />
      <Sidebar />
      <Box sx={{ position: "absolute", top: 20, right: 40, display: "flex", gap: 2, zIndex: 1200 }}>
        <Button
          variant="outlined"
          component="a"
          href="/quicktales_batch_template.csv"
          download="quicktales_batch_template.csv"
          sx={{
            color: "#00a7cd",
            borderColor: "#00a7cd",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            padding: "8px 20px",
            "&:hover": {
              borderColor: "#00acc1",
              backgroundColor: "rgba(0, 188, 212, 0.04)",
            },
          }}
        >
          Download Template
        </Button>
        <Button
          variant="outlined"
          component="a"
          href="https://docs.google.com/document/d/1DsHnAUA35ESFRx4psqJnxAl3WLDSp3Hm5AfRgxIZJRg/edit?tab=t.0#heading=h.p12tvbrkjygu"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "#00a7cd",
            borderColor: "#00a7cd",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            padding: "8px 20px",
            "&:hover": {
              borderColor: "#00acc1",
              backgroundColor: "rgba(0, 188, 212, 0.04)",
            },
          }}
        >
          QuickTales Batch Instructions
        </Button>

        <Button
          variant="outlined"
          onClick={() => navigate(routes.guestContributors)}
          sx={{
            color: "#00a7cd",
            borderColor: "#00a7cd",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            padding: "8px 20px",
            "&:hover": {
              borderColor: "#00acc1",
              backgroundColor: "rgba(0, 188, 212, 0.04)",
            },
          }}
        >
          Guest Contributors
        </Button>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenImportModal}
          disabled={isImportDisabled}
          sx={{
            backgroundColor: "#00a7cd",
            color: "#fff",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            padding: "10px 24px",
            "&:hover": {
              backgroundColor: "#00acc1",
            },
          }}
        >
          Import Batch
        </Button>
      </Box>
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>
          <FilterSection
            handleFilterModalOpen={() => setFilterModal(true)}
            statusFilterOptions={statusFilterOptions}
            createdDateFilterOptions={createdDateFilterOptions}
            handleDeleteChip={handleDeleteChip}
            archiveFilterOptions={archiveFilterOptions}
            toggleArchiveFilter={toggleArchiveFilter}
          />
          <SearchBar
            placeholder={"Search Batch Name, Status"}
            handleSearch={handleSearchBatches}
            selected={selected}
            handleArchiveToggle={handleArchiveToggle}
            handleGetLink={handleGetLink}
            handleSendLink={handleSendLink}
          />
          <Paper sx={{ width: "100%", mb: 2, mx: 2, mt: 1 }}>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} aria-labelledby="batchesTable" size="medium">
                <TableHeadComponent
                  TableHeadData={headCells}
                  numSelected={selected.length}
                  order={"desc"}
                  orderBy={"createdAt"}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={() => {}}
                  rowCount={paginatedBatches.length}
                />
                <TableBody>
                  {paginatedBatches.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={headCells.length + 1} align="center" sx={{ py: 5 }}>
                        No batches yet. Click "Import Batch" to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedBatches.map((batch, index) => {
                      const isItemSelected = isSelected(batch.id);
                      const labelId = `batch-table-checkbox-${index}`;
                      const createdAt = batch.createdAt?.toDate
                        ? batch.createdAt.toDate()
                        : batch.createdAt;

                      return (
                        <TableRow
                          key={batch.id}
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          selected={isItemSelected}
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleRowClick(batch)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                              onClick={(event) => handleClickCheckbox(event, batch.id)}
                            />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            id={labelId}
                            sx={{
                              color: "#00a7cd",
                              fontWeight: 500,
                              cursor: "pointer",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {batch.batchName}
                          </TableCell>
                          <TableCell>
                            {batch.description || ""}
                          </TableCell>
                          <TableCell>
                            {createdAt
                              ? new Date(createdAt).toLocaleString()
                              : ""}
                          </TableCell>
                          <TableCell>{batch.status || ""}</TableCell>
                          <TableCell>
                            {batch.archive === true ? (
                              <Box sx={{ color: "red", fontWeight: 500 }}>Archived</Box>
                            ) : (
                              <Box sx={{ color: "green", fontWeight: 500 }}>Active</Box>
                            )}
                          </TableCell>
                          <TableCell>{batch.quicktalesCount ?? 0}</TableCell>
                          <TableCell>{batch.quicktalesImported ?? 0}</TableCell>
                          <TableCell>{batch.quicktalesSkipped ?? 0}</TableCell>
                          <TableCell>
                            {batch.report && batch.report.length > 0 ? (
                              <Button
                                size="small"
                                onClick={(event) => handleViewReport(event, batch)}
                                sx={{
                                  color: "#00A7CD",
                                  textTransform: "none",
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  padding: 0,
                                  minWidth: "auto",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                View
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {batch?.createdByData
                              ? `${batch.createdByData.firstName ?? ""} ${batch.createdByData.lastName ?? ""}`.trim()
                              : ""}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePaginationSection
              count={Math.ceil(batches.length / rowsPerPage)}
              page={page}
              total={batches.length ?? 0}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>

      <ImportBatchModal
        open={importModalOpen}
        onClose={handleCloseImportModal}
        onImport={handleImportBatch}
        importing={importing}
        areas={areas}
        defaultAutoApprove={orgImportBatchAutoApprove}
      />

      <FilterModalComponent
        modalOpen={filterModal}
        handleClose={() => setFilterModal(false)}
        resetFilter={resetFilter}
        handleFilter={() => {
          handleFilterBatches();
          setFilterModal(false);
        }}
        data={[
          {
            mainOptions: archiveFilterOptions,
            setMainOptions: setArchiveFilterOptions,
            type: "text",
            title: "Archive Status",
            currentOptions: [
              { label: "All", selected: true },
              { label: "Active", selected: false },
              { label: "Archived", selected: false },
            ],
          },
          {
            mainOptions: statusFilterOptions,
            setMainOptions: setStatusFilterOptions,
            type: "text",
            title: "Status",
            currentOptions: [
              { label: "All", selected: true },
              { label: "completed", selected: false },
              { label: "processing", selected: false },
              { label: "failed", selected: false },
            ],
          },
          {
            mainOptions: createdDateFilterOptions,
            setMainOptions: setCreatedDateFilterOptions,
            type: "date",
            title: "Created At",
            currentOptions: [
              { label: "All", selected: true },
              { label: "Today", selected: false },
              { label: "Past Week", selected: false },
              { label: "Last 30 Days", selected: false },
              { label: "Last 12 Months", selected: false },
              { label: "Enter a Range", selected: false },
            ],
          },
        ]}
      />

      <BatchReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        batchName={selectedBatchName}
        report={selectedReport}
      />
    </Box>
  );
};

export default BatchesContainer;
